import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Target, Trophy, Plus, Users, Calendar, TrendingUp } from 'lucide-react';

const ChallengesGoals = () => {
  const navigate = useNavigate();
  const [challenges, setChallenges] = useState([]);
  const [goals, setGoals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('challenges');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [challengesRes, goalsRes] = await Promise.all([
        axios.get('/challenges'),
        axios.get('/goals')
      ]);
      setChallenges(challengesRes.data);
      setGoals(goalsRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const TabButton = ({ id, label, isActive, onClick }) => (
    <button
      onClick={() => onClick(id)}
      className={`px-4 py-2 font-medium rounded-lg transition-colors ${
        isActive 
          ? 'bg-blue-600 text-white' 
          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
      }`}
    >
      {label}
    </button>
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-3xl font-bold text-gray-900">Challenges & Goals</h2>
          <p className="text-gray-600 mt-2">Manage fitness challenges and user goals</p>
        </div>
        <button className="btn-primary flex items-center">
          <Plus className="h-5 w-5 mr-2" />
          Create Challenge
        </button>
      </div>

      {/* Tab Navigation */}
      <div className="flex space-x-2">
        <TabButton
          id="challenges"
          label="Challenges"
          isActive={activeTab === 'challenges'}
          onClick={setActiveTab}
        />
        <TabButton
          id="goals"
          label="Goals"
          isActive={activeTab === 'goals'}
          onClick={setActiveTab}
        />
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      ) : (
        <div className="card">
          {activeTab === 'challenges' ? (
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-gray-900">Active Challenges</h3>
              {challenges.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {challenges.map((challenge) => (
                    <div key={challenge.id} className="border border-gray-200 rounded-lg p-6">
                      <div className="flex items-center justify-between mb-4">
                        <Trophy className="h-8 w-8 text-yellow-500" />
                        <span className="text-sm text-gray-500">{challenge.duration}</span>
                      </div>
                      <h4 className="text-lg font-semibold text-gray-900 mb-2">{challenge.title}</h4>
                      <p className="text-sm text-gray-600 mb-4">{challenge.description}</p>
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span>Target:</span>
                          <span className="font-medium">{challenge.target_value} {challenge.type}</span>
                        </div>
                        <div className="flex justify-between">
                          <span>Group:</span>
                          <span className="font-medium">{challenge.group_name}</span>
                        </div>
                        <div className="flex justify-between">
                          <span>Participants:</span>
                          <span className="font-medium">{challenge.participant_count}</span>
                        </div>
                        <div className="flex justify-between">
                          <span>Period:</span>
                          <span className="font-medium">
                            {new Date(challenge.start_date).toLocaleDateString()} - {new Date(challenge.end_date).toLocaleDateString()}
                          </span>
                        </div>
                      </div>
                      <div className="mt-4 pt-4 border-t border-gray-200">
                        <button 
                          onClick={() => navigate(`/challenges/challenge/${challenge.id}/progress`)}
                          className="w-full btn-secondary text-sm"
                        >
                          View Progress
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <Trophy className="mx-auto h-12 w-12 text-gray-400" />
                  <h3 className="mt-2 text-sm font-medium text-gray-900">No challenges</h3>
                  <p className="mt-1 text-sm text-gray-500">Get started by creating a new challenge.</p>
                </div>
              )}
            </div>
          ) : (
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-gray-900">User Goals</h3>
              {goals.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="table-header">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Goal
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          User
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Target
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Status
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Created
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {goals.map((goal) => (
                        <tr key={goal.id} className="table-row">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              <Target className="h-5 w-5 text-blue-500 mr-3" />
                              <div>
                                <div className="text-sm font-medium text-gray-900">{goal.title}</div>
                                <div className="text-sm text-gray-500">{goal.description}</div>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900">{goal.user_name}</div>
                            <div className="text-sm text-gray-500">{goal.user_city}</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900">
                              {goal.target_value} {goal.type}
                            </div>
                            <div className="text-sm text-gray-500">{goal.duration}</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                              goal.is_active 
                                ? 'bg-green-100 text-green-800' 
                                : 'bg-gray-100 text-gray-800'
                            }`}>
                              {goal.is_active ? 'Active' : 'Inactive'}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {new Date(goal.created_at).toLocaleDateString()}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <button 
                              onClick={() => navigate(`/challenges/goal/${goal.id}/progress`)}
                              className="text-blue-600 hover:text-blue-900 text-sm font-medium"
                            >
                              View Progress
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-12">
                  <Target className="mx-auto h-12 w-12 text-gray-400" />
                  <h3 className="mt-2 text-sm font-medium text-gray-900">No goals</h3>
                  <p className="mt-1 text-sm text-gray-500">Users haven't set any goals yet.</p>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ChallengesGoals; 